// ============================================================================
// Budget module — types
// ----------------------------------------------------------------------------
// Soft-coupled to Timeline. Amounts are stored as integer minor units of the
// workspace's single ISO currency (workspace.settings.currency) to keep the
// recursive transactional aggregation arithmetic exact.
// ============================================================================

import type { Timestamp } from 'firebase/firestore'

export const BUDGET_CATEGORIES = ['Labor', 'Materials', 'Travel', 'Vendors', 'Software', 'Other'] as const
export type BudgetCategory = (typeof BUDGET_CATEGORIES)[number]
export const DEFAULT_BUDGET_CATEGORY: BudgetCategory = 'Other'

export const normalizeBudgetCategory = (value: unknown): BudgetCategory =>
  typeof value === 'string' && (BUDGET_CATEGORIES as readonly string[]).includes(value)
    ? value as BudgetCategory
    : DEFAULT_BUDGET_CATEGORY

/** Top-level expense collection keyed by workspaceId via a `workspaceId` field
 * on each doc. Path: `workspace_expenses/{expenseId}`. */
export const WORKSPACE_EXPENSES_COLLECTION = 'workspace_expenses'

/** Pre-aggregated workspace-wide summary doc, one per workspace, maintained
 * inside the same transaction as every expense write. Powers the Budget Hub
 * Dashboard without iterating the full ledger on the client. */
export const WORKSPACE_BUDGET_SUMMARY_COLLECTION = 'workspace_budget_summary'

/** Rolling-window granularity for burn-rate chart. */
export type BurnRateWindow = 30 | 60 | 90

export interface BurnRateBucket {
  /** YYYY-MM-DD (workspace timezone, falls back to UTC). */
  date: string
  /** Net spend for the day in integer minor units (positive after reversals). */
  spend: number
  /** Number of distinct expense entries logged that day (reversals counted). */
  entries: number
}

export interface TopSpenderEntry {
  entityId: string
  entityType: BudgetEntityType
  title: string
  /** Aggregate spend in integer minor units. For Goals this is the recursive
   * rollup; for Tasks the rollup of own + descendant Checklists. */
  spend: number
  budgetLimit: number | null
  /** spend / budgetLimit as a fraction (e.g. 0.78). Null if no limit set. */
  percentConsumed: number | null
}

export interface WorkspaceBudgetSummary {
  workspaceId: string
  currency: string
  /** Sum of Goal-level budgetLimits only. Avoids the parent-child
   * double-counting that would arise from summing every node. */
  totalBudgetLimit: number
  /** Sum of Goal-level actualSpend (which is itself a recursive roll-up). */
  totalActualSpend: number
  /** Sum of Goal-level estimatedCost. */
  totalEstimatedCost: number
  /** Last 90 days of net spend buckets, ordered oldest-first. */
  dailyBuckets: BurnRateBucket[]
  /** Top 20 by absolute spend across Goals + Tasks. */
  topSpenders: TopSpenderEntry[]
  /** Number of nodes (Goals + Tasks) whose `actualSpend > budgetLimit`. */
  overBudgetCount: number
  /** Cumulative count of `expense.exceeded` audit events. */
  breachCount: number
  /** Cumulative count of `budget.override` audit events. */
  overrideCount: number
  lastUpdatedAt: Timestamp | null
}

export type BudgetEntityType = 'GOAL' | 'TASK' | 'CHECKLIST'

/** Caller-supplied ancestor descriptor. The budgetService transaction reads
 * each ref to fetch the current `actualSpend` (per the ratified rule "Read
 * parentId") and writes back the new aggregate. Ordered nearest-to-root, so
 * `chain[0]` is the leaf the expense is logged against. */
export interface BudgetEntityRef {
  type: BudgetEntityType
  id: string
  /** Firestore document path, e.g. `checklists/abc123`. The caller knows
   * which collection holds each entity so the service stays data-agnostic. */
  docPath: string
}

export interface WorkspaceExpenseReceipt {
  /** Firebase Storage path (primary). */
  storagePath?: string | null
  /** External URL (optional secondary). */
  externalUrl?: string | null
}

export interface WorkspaceExpense {
  id: string
  workspaceId: string
  /** Integer minor units. Reversal entries are NEGATIVE. */
  amount: number
  /** ISO 4217 code — must match `workspace.settings.currency` at log time. */
  currency: string
  description: string
  category: BudgetCategory
  linkedEntityId: string
  linkedEntityType: BudgetEntityType
  /** Ordered ancestor chain (leaf-first) at the moment of logging. Frozen on
   * the entry so breadcrumb navigation still works if the entity is renamed. */
  linkedEntityPath: Array<{ type: BudgetEntityType; id: string; title: string }>
  /** Present only on reversal entries. Points at the original `expense.log`
   * entry being reversed. Reversals also carry a negative `amount`. */
  reversesExpenseId?: string | null
  receipt?: WorkspaceExpenseReceipt | null
  createdById: string
  createdByName?: string | null
  timestamp: Timestamp
  // Slice B1 optional fields
  vendorId?: string | null
  tags?: string[]
  taxAmount?: number | null
  taxInclusive?: boolean
  paymentMethod?: 'cash' | 'card' | 'bank' | 'upi' | 'other' | null
  attachments?: string[]
  recurringId?: string | null
  isIncome?: boolean
}

export type BudgetAuditAction =
  | 'budget.create'      // budgetLimit set on a node where it was previously null
  | 'budget.update'      // budgetLimit changed on a node
  | 'budget.override'    // OWNER force-completed an over-budget Task/Goal
  | 'expense.log'        // Normal positive expense entry recorded
  | 'expense.reverse'    // Reversal entry recorded (negative amount + reversesExpenseId)
  | 'expense.exceeded'   // The current entry pushed actualSpend past budgetLimit
  | 'vendor.create'
  | 'vendor.update'
  | 'vendor.delete'
  | 'recurring.create'
  | 'recurring.update'
  | 'recurring.deactivate'
  | 'category_limit.upsert'
  | 'category_limit.delete'
  | 'expense.approve'
  | 'expense.reject'
  | 'budget.bulk.import'

export interface BudgetCategoryLimit {
  id: string
  workspaceId: string
  category: BudgetCategory
  monthlyLimit?: number | null
  quarterlyLimit?: number | null
  yearlyLimit?: number | null
  createdAt?: Timestamp | null
  updatedAt?: Timestamp | null
  updatedById?: string | null
}

export class BudgetCategoryLimitExceededError extends Error {
  category: BudgetCategory
  window: 'monthly' | 'quarterly' | 'yearly'
  cap: number
  wouldSpend: number

  constructor(
    category: BudgetCategory,
    window: 'monthly' | 'quarterly' | 'yearly',
    cap: number,
    wouldSpend: number,
  ) {
    super(
      `Category ${category} limit exceeded for ${window} window (cap: ${cap}, spend would be: ${wouldSpend})`,
    )
    this.name = 'BudgetCategoryLimitExceededError'
    this.category = category
    this.window = window
    this.cap = cap
    this.wouldSpend = wouldSpend
  }
}

export type BudgetEnforcementMode = 'warn' | 'block'

/** Color-band thresholds for the progress bar. */
export const BUDGET_BAND_AMBER = 0.8
export const BUDGET_BAND_RED = 1.0

export type BudgetProgressBand = 'green' | 'amber' | 'red'

export const computeBudgetBand = (
  actualSpend: number,
  budgetLimit: number | null | undefined,
): BudgetProgressBand => {
  if (!budgetLimit || budgetLimit <= 0) return 'green'
  const ratio = actualSpend / budgetLimit
  if (ratio >= BUDGET_BAND_RED) return 'red'
  if (ratio >= BUDGET_BAND_AMBER) return 'amber'
  return 'green'
}

/** Progress-bar reference mode toggled inside the Financials tab. */
export type BudgetProgressReference = 'budgetLimit' | 'estimatedCost' | 'actualSpend'
